/**
 * Today's shortlist, the swipe deck (.scratch/swipe-deck/ tickets 03, 04, 13;
 * contract gap 32). Signed-in only; the population is Recommended's. The Ready
 * tray's read, `GET /v1/deck/applications`, left with the tray (action-tab
 * ticket 15); what a right swipe became is Action's list.
 */
import { api } from './client';
import type { DeckDecision, DeckTasteReset, DeckDecisionKind, DeckPassReason, DeckRequirements, DeckRequirementsPut, DeckResponse } from './types';

export const deckApi = {
  /**
   * One batch of the deck and the header's counters (ticket 13). `limit` is 0 to 20 (the server's default
   * is 10): 0 answers `total` and the counters with no cards, which is how a decision refreshes them.
   * `cursor` is the previous batch's `nextCursor`, passed back untouched; the batch is the undecided
   * cards strictly after it in deck order (newest posting first), so a decision made in between never
   * shifts or repeats one. Since 2026-10-10 a saved job is decided like a passed or an applied one: it is a
   * Job Queue row, so it is in no batch and not in `total`, and no card arrives with `decision: 'save'`.
   */
  get: (opts?: { limit?: number; cursor?: string | null }) =>
    api<DeckResponse>('/v1/deck', { query: { limit: opts?.limit, cursor: opts?.cursor ?? undefined } }),
  /**
   * Records a decision. `apply` starts the tailor run the job page would, at the
   * recommended level, reserving one credit — hence the `Idempotency-Key` — and
   * answers 402 `insufficient_credits` at a short balance with nothing written.
   * 201 for a new row, 200 for the same decision again, 409 for a different one
   * (undo first), 404 for a job that is not publishable.
   */
  decide: (jobId: string, decision: DeckDecisionKind, idempotencyKey: string) =>
    api<DeckDecision>(`/v1/deck/${encodeURIComponent(jobId)}`, { method: 'POST', body: { decision }, idempotencyKey }),
  /** Why a card was passed (shortlist-relevance 07, 204): hides its role, places or company, or moves the seniority range. 404 without a pass on file. */
  passReason: (jobId: string, reason: DeckPassReason) =>
    api<void>(`/v1/deck/${encodeURIComponent(jobId)}/reason`, { method: 'POST', body: { reason } }),
  /** Undoes a pass or a save (204), which puts the job back in the deck, undecided. 404 with none; 409 for an apply, whose run is already queued. */
  undo: (jobId: string) => api<void>(`/v1/deck/${encodeURIComponent(jobId)}`, { method: 'DELETE' }),
  /** The Hard requirements (shortlist-ranking ticket 05, gap 41): the person's own, or inferred from the master résumé where unset. */
  requirements: () => api<DeckRequirements>('/v1/deck/requirements'),
  /** A key left out keeps what is held; answers the requirements as stored. */
  putRequirements: (body: DeckRequirementsPut) => api<DeckRequirements>('/v1/deck/requirements', { method: 'PUT', body }),
  /** Taste reset (ticket 10, gap 44): no body; decisions made before `resetAt` stop shaping the order. */
  resetTaste: () => api<DeckTasteReset>('/v1/deck/taste/reset', { method: 'POST' }),
};
